import Head from 'next/head'
import { useRouter } from 'next/router'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'
import { getOrigin } from '@/lib/window'
import { cdnImage } from '@/lib/image-loader'

/** The site's own address for share tags and canonical links (NEXT_PUBLIC_SITE_URL in Secrets, else the page's origin). */
export const siteOrigin = (): string => (process.env.NEXT_PUBLIC_SITE_URL || getOrigin()).replace(/\/$/, '')

/** The share image shown when a page names none: the Suvaani logo, resized by the image optimiser (not the 1.5 MB original). */
const DEFAULT_IMAGE = '/images/brand/suvaani.png'

/**
 * A share image as an absolute address, resized so a 2 MB illustration becomes a light preview: on the CDN in
 * production (where /_next/image is off, see next.config.ts), through the image optimiser otherwise. Remote addresses
 * are kept as they are.
 */
export const shareImage = (src: string, width = 1200): string => {
    if (/^https?:\/\//.test(src)) return src
    if (process.env.NODE_ENV === 'production' && src.startsWith('/images/')) return cdnImage(src, width, 75, 'original')

    return `${siteOrigin()}/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=75`
}

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    site = siteConfig.name,
    noIndex = false
}): ReactNode => {
    const router = useRouter()
    const pageTitle = title ? `${title} - ${site}` : site
    const path = (router.asPath || '/').split(/[?#]/)[0]
    const url = `${siteOrigin()}${path === '/' ? '' : path}` || path
    const image = shareImage(ogImage ?? DEFAULT_IMAGE, ogImage ? 1200 : 640)

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            {noIndex ? <meta name='robots' content='noindex, nofollow' /> : <link rel='canonical' href={url} />}
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content='Suvaani' />
            <meta property='og:url' content={url} />
            <meta property='og:image' content={image} />
            <meta property='og:image:alt' content={pageTitle} />
            <meta name='twitter:card' content={ogImage ? 'summary_large_image' : 'summary'} />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={image} />
        </Head>
    )
}
